import React, { useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

const DATE_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

type DateDraft = {
  day: number;
  month: number;
  year: number;
};

interface DatePickerModalProps {
  visible: boolean;
  title?: string;
  initialValue?: string;
  onCancel: () => void;
  onConfirm: (dateValue: string) => void;
  accentColor?: string;
  yearsBack?: number;
  yearsForward?: number;
}

const parseDateValue = (value?: string): Date => {
  if (!value) return new Date();
  const normalized = value.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? value.trim();
  const parts = normalized.split('-');
  if (parts.length !== 3) return new Date();
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
};

export const DatePickerModal: React.FC<DatePickerModalProps> = ({
  visible,
  title = 'Select date',
  initialValue,
  onCancel,
  onConfirm,
  accentColor = '#2563eb',
  yearsBack = 18,
  yearsForward = 0,
}) => {
  const [dateDraft, setDateDraft] = useState<DateDraft>(() => {
    const parsed = parseDateValue(initialValue);
    return {
      day: parsed.getDate(),
      month: parsed.getMonth() + 1,
      year: parsed.getFullYear(),
    };
  });

  useEffect(() => {
    if (!visible) return;
    const parsed = parseDateValue(initialValue);
    setDateDraft({
      day: parsed.getDate(),
      month: parsed.getMonth() + 1,
      year: parsed.getFullYear(),
    });
  }, [initialValue, visible]);

  const daysInDraftMonth = new Date(dateDraft.year, dateDraft.month, 0).getDate();

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const total = yearsBack + yearsForward + 1;
    return Array.from({ length: total }, (_, index) => currentYear + yearsForward - index);
  }, [yearsBack, yearsForward]);

  const handleConfirm = () => {
    const year = String(dateDraft.year);
    const month = String(dateDraft.month).padStart(2, '0');
    const day = String(dateDraft.day).padStart(2, '0');
    onConfirm(`${year}-${month}-${day}`);
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View style={styles.container}>
          <Text style={styles.title}>{title}</Text>
          <View style={styles.selectionContainer}>
            <View style={styles.column}>
              <Text style={styles.columnLabel}>Day</Text>
              <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                {Array.from({ length: daysInDraftMonth }, (_, index) => index + 1).map((day) => (
                  <TouchableOpacity
                    key={day}
                    style={[styles.optionButton, dateDraft.day === day && styles.optionButtonSelected]}
                    onPress={() => setDateDraft((current) => ({ ...current, day }))}
                  >
                    <Text style={[styles.optionText, dateDraft.day === day && { color: accentColor, fontWeight: '700' }]}>{day}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>

            <View style={styles.column}>
              <Text style={styles.columnLabel}>Month</Text>
              <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                {DATE_MONTHS.map((monthLabel, index) => {
                  const month = index + 1;
                  return (
                    <TouchableOpacity
                      key={monthLabel}
                      style={[styles.optionButton, dateDraft.month === month && styles.optionButtonSelected]}
                      onPress={() => {
                        const nextDays = new Date(dateDraft.year, month, 0).getDate();
                        setDateDraft((current) => ({
                          ...current,
                          month,
                          day: Math.min(current.day, nextDays),
                        }));
                      }}
                    >
                      <Text style={[styles.optionText, dateDraft.month === month && { color: accentColor, fontWeight: '700' }]}>{monthLabel}</Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            </View>

            <View style={styles.column}>
              <Text style={styles.columnLabel}>Year</Text>
              <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                {years.map((year) => (
                  <TouchableOpacity
                    key={year}
                    style={[styles.optionButton, dateDraft.year === year && styles.optionButtonSelected]}
                    onPress={() => {
                      const nextDays = new Date(year, dateDraft.month, 0).getDate();
                      setDateDraft((current) => ({
                        ...current,
                        year,
                        day: Math.min(current.day, nextDays),
                      }));
                    }}
                  >
                    <Text style={[styles.optionText, dateDraft.year === year && { color: accentColor, fontWeight: '700' }]}>{year}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </View>

          <View style={styles.preview}>
            <Text style={styles.previewText}>
              Selected: {new Date(dateDraft.year, dateDraft.month - 1, dateDraft.day).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Text>
          </View>

          <View style={styles.buttonRow}>
            <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.okButton, { backgroundColor: accentColor }]} onPress={handleConfirm}>
              <Text style={styles.okButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#ffffff',
    borderRadius: 18,
    padding: 16,
    elevation: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1e293b',
    textAlign: 'center',
    marginBottom: 12,
  },
  selectionContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  column: {
    flex: 1,
  },
  columnLabel: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: '600',
    marginBottom: 6,
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  scrollView: {
    maxHeight: 220,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    backgroundColor: '#f8fafc',
  },
  optionButton: {
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  optionButtonSelected: {
    backgroundColor: '#eef2ff',
  },
  optionText: {
    fontSize: 13,
    color: '#334155',
    textAlign: 'center',
  },
  preview: {
    marginTop: 12,
    padding: 10,
    borderRadius: 12,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  previewText: {
    fontSize: 14,
    color: '#334155',
    textAlign: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 14,
  },
  cancelButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: '#e2e8f0',
  },
  cancelButtonText: {
    color: '#334155',
    fontSize: 13,
    fontWeight: '600',
  },
  okButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 10,
  },
  okButtonText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
});

export default DatePickerModal;
